import type { Feature, User } from '@shady-pines/db';
import type { Uuid } from '@shady-pines/types';
import { isNil } from 'lodash';
import type { StoreApi } from 'zustand';
import { supabaseApi } from '../api/api-store';
import type { GlobalContextState } from './types';

export type GlobalContextApi = ReturnType<typeof GlobalContextApi>;

export const GlobalContextApi = (
  get: StoreApi<GlobalContextState>['getState'],
  set: StoreApi<GlobalContextState>['setState'],
) => ({
  fetchConfigurations: async () => {
    const loading = get().loading.configurations;
    if (loading) {
      return;
    }

    set(
      (s) =>
        ({
          loading: {
            ...s.loading,
            configurations: true,
          },
        }) as const satisfies Partial<GlobalContextState>,
    );

    const { data: configurations } = await supabaseApi()
      .from('configurations')
      .select('*')
      .is('deleted_at', null);

    set(
      (s) =>
        ({
          configurations: configurations ?? [],
          loading: {
            ...s.loading,
            configurations: false,
          },
        }) as const satisfies Partial<GlobalContextState>,
    );
  },
  fetchUserFeatures: async (userId: Uuid<User> | null) => {
    if (isNil(userId)) {
      set({
        userFeatures: [],
      } as const satisfies Partial<GlobalContextState>);
      return;
    }

    const loading = get().loading.userFeatures;
    if (loading) {
      return;
    }

    set(
      (s) =>
        ({
          loading: {
            ...s.loading,
            userFeatures: true,
          },
        }) as const satisfies Partial<GlobalContextState>,
    );

    const { data: userFeatures } = await supabaseApi()
      .from('features')
      .select('*')
      .is('deleted_at', null)
      .eq('user_id', userId);

    set(
      (s) =>
        ({
          loading: {
            ...s.loading,
            userFeatures: false,
          },
          userFeatures: (userFeatures as Feature<'user'>[] | null) ?? [],
        }) as const satisfies Partial<GlobalContextState>,
    );
  },
  fetchGlobalFeatures: async () => {
    set(
      (s) =>
        ({
          loading: {
            ...s.loading,
            globalFeatures: true,
          },
        }) as const satisfies Partial<GlobalContextState>,
    );

    const { data: globalFeatures } = await supabaseApi()
      .from('features')
      .select('*')
      .is('deleted_at', null)
      .is('user_id', null);

    set(
      (s) =>
        ({
          loading: {
            ...s.loading,
            globalFeatures: false,
          },
          globalFeatures: (globalFeatures as Feature<'global'>[] | null) ?? [],
        }) as const satisfies Partial<GlobalContextState>,
    );
  },
});
